// Muestra el texto de tareas y notas (markdown sencillo) de forma legible: parrafos, listas, **negrita** y enlaces.
// No inserta HTML: todo se arma con elementos de React, asi que un texto no puede meter codigo en la pagina.
import type { ReactNode } from 'react';

function enLinea(texto: string): ReactNode[] {
  const partes: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g;
  let ultimo = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(texto))) {
    if (m.index > ultimo) partes.push(texto.slice(ultimo, m.index));
    const t = m[0];
    partes.push(
      t.startsWith('**') ? (
        <b key={m.index}>{t.slice(2, -2)}</b>
      ) : (
        <a key={m.index} href={t} target="_blank" rel="noreferrer noopener">
          {t}
        </a>
      ),
    );
    ultimo = m.index + t.length;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes;
}

export function Texto({ texto }: { texto: string }) {
  const bloques: ReactNode[] = [];
  type Lista = { tipo: 'ul' | 'ol'; items: string[] };
  let lista = null as Lista | null;
  let parrafo: string[] = [];
  const cerrarParrafo = () => {
    if (parrafo.length) bloques.push(<p key={bloques.length}>{parrafo.flatMap((l, i) => (i ? [<br key={i} />, ...enLinea(l)] : enLinea(l)))}</p>);
    parrafo = [];
  };
  const cerrarLista = () => {
    const l = lista as Lista | null;
    if (l) {
      const items = l.items.map((it, i) => <li key={i}>{enLinea(it)}</li>);
      bloques.push(l.tipo === 'ul' ? <ul key={bloques.length}>{items}</ul> : <ol key={bloques.length}>{items}</ol>);
    }
    lista = null;
  };
  for (const linea of texto.replace(/\r/g, '').split('\n')) {
    const ul = /^\s*[-*•]\s+(.*)$/.exec(linea);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(linea);
    if (ul || ol) {
      cerrarParrafo();
      const tipo = ul ? 'ul' : 'ol';
      if (lista?.tipo !== tipo) cerrarLista();
      lista ??= { tipo, items: [] };
      lista.items.push((ul ?? ol)![1]);
    } else if (!linea.trim()) {
      cerrarParrafo();
      cerrarLista();
    } else {
      cerrarLista();
      parrafo.push(linea.replace(/^#+\s*/, ''));
    }
  }
  cerrarParrafo();
  cerrarLista();
  return <div className="texto">{bloques}</div>;
}

// Una linea corta sin marcas (para resumenes en listas): quita **, viñetas, numeracion y titulos.
export function textoPlano(texto: string, max = 160) {
  const t = texto
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^\s*(?:[-*•]|\d+[.)]|#+)\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return t.length > max ? `${t.slice(0, max)}…` : t;
}
